import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiUrl } from 'utils/apiUrl';
import { getSiteDisplayStatus } from '../utils';

interface SiteResponse {
  captain_id: string;
  learn_id?: string;
  learn_url?: string;
  client_name?: string;
  fleet_name?: string;
  aws_account?: string;
  account_name?: string;
  tenant_id?: string;
  upgrade_job_id?: string;
  upgrade_job_status?: string;
  reminder_sent?: boolean;
  reminder_sent_at?: string;
  [key: string]: any;
}

interface JobStatusData {
  jobStatus: string;
  error?: string;
  cause?: string;
}

export type SiteSortKey = 'captain_id' | 'learn_url' | 'status';
export type SortDirection = 'asc' | 'desc';

export interface SiteSortConfig {
  key: SiteSortKey;
  direction: SortDirection;
}

interface UseCohortDetailLogicOptions {
  cohortId: string | null;
  sites: SiteResponse[];
  pageSize?: number;
  enableNotifications?: boolean;
}

export const useCohortDetailLogic = ({
  cohortId,
  sites,
  pageSize = 5,
  enableNotifications = true,
}: UseCohortDetailLogicOptions) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatusData>>({});

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<SiteSortConfig | null>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [removeSiteConfirmOpen, setRemoveSiteConfirmOpen] = useState(false);
  const [siteToRemove, setSiteToRemove] = useState<string | null>(null);
  const [deleteCohortConfirmOpen, setDeleteCohortConfirmOpen] = useState(false);

  const statusFilterNarrowedRef = useRef(false);

  useEffect(() => {
    setPageIndex(0);
  }, [sites]);

  useEffect(() => {
    statusFilterNarrowedRef.current = false;
    if (cohortId) {
      setPageIndex(0);
      setJobStatuses({});
      setSearchText('');
      setStatusFilter([]);
      setSortConfig(null);
    }
  }, [cohortId]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchText, statusFilter, sortConfig]);

  const fetchJobStatuses = useCallback(
    async (sitesToCheck: SiteResponse[], migrationStatus?: string) => {
      if (!sitesToCheck.length || migrationStatus !== 'IN_PROGRESS') return;

      const jobIdsToFetch = sitesToCheck
        .map((site) => site.upgrade_job_id)
        .filter((id) => id && !jobStatuses[id]) as string[];

      if (jobIdsToFetch.length === 0) return;

      try {
        const promises = jobIdsToFetch.map(async (executionId) => {
          try {
            const response = await fetch(
              apiUrl('lct', `jobs/MigrateCustomerData/executions/${executionId}`),
            );
            if (!response.ok) throw new Error(response.statusText);
            const jobData = await response.json();
            return {
              id: executionId,
              data: {
                jobStatus: jobData.status,
                error: jobData.error,
                cause: jobData.cause,
              },
            };
          } catch (error: any) {
            return {
              id: executionId,
              data: {
                jobStatus: 'UNKNOWN',
                error: error.message,
              },
            };
          }
        });

        const results = await Promise.all(promises);
        const newJobStatuses = { ...jobStatuses };
        results.forEach(({ id, data }) => {
          newJobStatuses[id] = data;
        });
        setJobStatuses(newJobStatuses);
      } catch (error) {
        console.error('Failed to fetch job statuses:', error);
      }
    },
    [jobStatuses],
  );

  const availableStatuses = useMemo(() => {
    const statuses = new Set<string>();
    sites.forEach((site) => statuses.add(getSiteDisplayStatus(site, enableNotifications)));
    return Array.from(statuses).sort((a, b) => a.localeCompare(b));
  }, [sites, enableNotifications]);

  useEffect(() => {
    if (!cohortId || availableStatuses.length === 0) return;

    if (statusFilterNarrowedRef.current) {
      setStatusFilter((prev) => {
        const next = prev.filter((status) => availableStatuses.includes(status));
        return next.length === prev.length ? prev : next;
      });
      return;
    }

    setStatusFilter((prev) =>
      prev.length === availableStatuses.length &&
      prev.every((status) => availableStatuses.includes(status))
        ? prev
        : availableStatuses,
    );
  }, [cohortId, availableStatuses]);

  const handleStatusFilterChange = useCallback(
    (next: string[]) => {
      statusFilterNarrowedRef.current = next.length > 0 && next.length < availableStatuses.length;
      setStatusFilter(next);
    },
    [availableStatuses],
  );

  const filteredSites = useMemo(() => {
    let result = sites;

    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (site) =>
          site.captain_id?.toLowerCase().includes(query) ||
          site.learn_url?.toLowerCase().includes(query),
      );
    }

    if (statusFilter.length) {
      result = result.filter((site) =>
        statusFilter.includes(getSiteDisplayStatus(site, enableNotifications)),
      );
    }

    if (sortConfig) {
      const { key, direction } = sortConfig;
      const factor = direction === 'asc' ? 1 : -1;
      const getValue = (site: SiteResponse): string => {
        if (key === 'status') return getSiteDisplayStatus(site, enableNotifications);
        return site[key] || '';
      };
      result = [...result].sort(
        (a, b) =>
          getValue(a).localeCompare(getValue(b), undefined, { sensitivity: 'base' }) * factor,
      );
    }

    return result;
  }, [sites, searchText, statusFilter, sortConfig, enableNotifications]);

  const hasActiveFilters =
    searchText.trim() !== '' ||
    (statusFilter.length > 0 && statusFilter.length < availableStatuses.length);

  const totalPages = filteredSites.length ? Math.ceil(filteredSites.length / pageSize) : 0;
  const safePageIndex = totalPages > 0 ? Math.min(pageIndex, totalPages - 1) : 0;

  const paginatedSites = useMemo(() => {
    if (!filteredSites.length) return [];
    const startIndex = safePageIndex * pageSize;
    return filteredSites.slice(startIndex, startIndex + pageSize);
  }, [filteredSites, safePageIndex, pageSize]);

  const filteredSitesCount = filteredSites.length;
  const visibleSitesCount = sites.length;

  const siteToRemoveInfo = useMemo(
    () => sites.find((site) => site.captain_id === siteToRemove),
    [sites, siteToRemove],
  );

  const handleNotificationClick = useCallback(() => setConfirmDialogOpen(true), []);
  const handleReminderClick = useCallback(() => setReminderDialogOpen(true), []);
  const handleRemoveSiteRequest = useCallback((siteId: string) => {
    setSiteToRemove(siteId);
    setRemoveSiteConfirmOpen(true);
  }, []);
  const handleDeleteCohortRequest = useCallback(() => setDeleteCohortConfirmOpen(true), []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(Math.max(0, Math.min(newPage, totalPages - 1)));
    },
    [totalPages],
  );

  const handleSort = useCallback((key: SiteSortKey) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const clearFilters = useCallback(() => {
    statusFilterNarrowedRef.current = false;
    setSearchText('');
    setStatusFilter(availableStatuses);
  }, [availableStatuses]);

  const getVisiblePaginatedSites = useCallback(
    (): (SiteResponse & { cohort: string })[] =>
      paginatedSites.map((site) => ({
        ...site,
        cohort: cohortId || '',
      })),
    [paginatedSites, cohortId],
  );

  const getSitesForComponents = useCallback(
    () =>
      sites.map((site) => ({
        captain_id: site.captain_id,
        learn_url: site.learn_url,
        site_status: site.site_status,
        notifications: site.notifications,
        tenant_id: site.tenant_id,
      })),
    [sites],
  );

  return {
    pageIndex: safePageIndex,
    jobStatuses,
    visibleSitesCount,
    filteredSitesCount,
    totalPages,
    siteToRemoveInfo,
    confirmDialogOpen,
    reminderDialogOpen,
    removeSiteConfirmOpen,
    deleteCohortConfirmOpen,
    searchText,
    statusFilter,
    sortConfig,
    availableStatuses,
    hasActiveFilters,
    handleNotificationClick,
    handleReminderClick,
    handleRemoveSiteRequest,
    handleDeleteCohortRequest,
    setPageIndex: handlePageChange,
    setConfirmDialogOpen,
    setReminderDialogOpen,
    setRemoveSiteConfirmOpen,
    setDeleteCohortConfirmOpen,
    setSearchText,
    setStatusFilter: handleStatusFilterChange,
    handleSort,
    clearFilters,
    getVisiblePaginatedSites,
    getSitesForComponents,
    fetchJobStatuses,
  };
};
